import React, { ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { Redirect, usePathname } from "expo-router";
import { useAuth } from "./AuthContext";
import { Spinner } from "@/components";
import { User } from "@/store/types";
import { colors } from "@/constants";
import agreementsData from "@/assets/agreements/agreements.json";

/**
 * Drawer screens that authenticated, onboarded users may sit on directly.
 * Any other pathname under (drawer) is treated as an onboarding-in-progress
 * waypoint and redirects to /chat once auth resolves.
 */
const TERMINAL_ROUTES: readonly string[] = [
  "/account",
  "/admin",
  "/chat",
  "/facts",
  "/patterns",
  "/settings"
];

/**
 * Decide where the user belongs given the current auth state and pathname.
 * Returns the literal sentinel "spinner" while auth is still resolving, or
 * a pathname (without the (drawer) group prefix) representing the canonical
 * destination. The caller renders children when the destination equals the
 * current pathname.
 */
function authGateTarget(
  user: User | null,
  isLoading: boolean,
  pathname: string
): "spinner" | string {
  // While auth is resolving, keep the login screen rendered so its own
  // in-progress states (Signing in… / verification card) stay visible.
  // Every other drawer route shows a full-screen spinner instead of a
  // half-mounted, click-reactive shell.
  if (isLoading) return pathname === "/login" ? pathname : "spinner";

  if (!user) return "/login";

  const needsConsent =
    !user.consent.hasConsented ||
    user.consent.consentVersion !== agreementsData.version;
  if (needsConsent) return "/consent";
  if (!user.settings?.voice?.selectedVoiceId) return "/settings";

  return TERMINAL_ROUTES.includes(pathname) ? pathname : "/chat";
}

/**
 * Props for the AuthGate component.
 */
interface AuthGateProps {
  /** Drawer subtree to guard. Stays mounted across the loading transition so
   *  the URL (including query params like ?session=…) is preserved. */
  children: ReactNode;
}

/**
 * Render-time auth gate for the (drawer) route group.
 *
 * Replaces the effect-based useProtectedRoute so protected screens never
 * mount in an unresolved state — closing the window in which a user could
 * click "Chat" before useAuth has hydrated the context user (Firebase token
 * refresh → /auth/validate → Firestore getUser).
 *
 * Important: while loading, children render with a full-screen spinner
 * overlaid on top — they are NOT unmounted. Unmounting the Drawer caused
 * react-navigation to re-mount with its alphabetically-first screen as the
 * initial route, dropping the original URL and query string on re-mount.
 */
export function AuthGate({ children }: AuthGateProps) {
  const { user, isLoading } = useAuth();
  const pathname = usePathname();
  const target = authGateTarget(user, isLoading, pathname);

  // Auth/onboarding mismatch — change the URL intentionally. Drawer will
  // unmount and re-mount at the new route; original URL is being abandoned
  // on purpose so loss of query string is fine here.
  if (target !== "spinner" && target !== pathname) {
    return <Redirect href={`/(drawer)${target}` as never} />;
  }

  return (
    <View style={styles.root}>
      {children}
      {target === "spinner" && <Spinner style={styles.spinnerOverlay} />}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1
  },
  spinnerOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.surface
  }
});
